import React from "react";
import { Box } from "@mui/material";

// The hero scene. Built as inline SVG rather than a bitmap: it renders with the
// page, scales to any screen, and costs nothing to download — the only photo in
// the project is 1.8 MB, which is what made the site feel heavy before.
const HeroArt = () => (
  <Box
    aria-hidden
    sx={{
      width: "100%",
      maxWidth: "620px",
      userSelect: "none",
      "@keyframes floaty": {
        "0%,100%": { transform: "translateY(0)" },
        "50%": { transform: "translateY(-10px)" },
      },
      "@keyframes pulseGlow": {
        "0%,100%": { opacity: 0.55 },
        "50%": { opacity: 1 },
      },
      "& .float-a": { animation: "floaty 6s ease-in-out infinite" },
      "& .float-b": { animation: "floaty 7.5s ease-in-out infinite 1s" },
      "& .float-c": { animation: "floaty 8.5s ease-in-out infinite .5s" },
      "& .glow": { animation: "pulseGlow 4s ease-in-out infinite" },
    }}
  >
    <svg viewBox="0 0 620 520" width="100%" height="auto" role="img">
      <defs>
        <linearGradient id="hBrand" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#38BDF8" />
          <stop offset="55%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#A855F7" />
        </linearGradient>
        <linearGradient id="hGlass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity=".14" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity=".05" />
        </linearGradient>
        <radialGradient id="hCore">
          <stop offset="0%" stopColor="#A855F7" stopOpacity=".95" />
          <stop offset="55%" stopColor="#3B82F6" stopOpacity=".45" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
        </radialGradient>
        <filter id="hBlur" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="26" />
        </filter>
      </defs>

      {/* the glow behind everything */}
      <circle className="glow" cx="330" cy="230" r="150" fill="url(#hCore)" filter="url(#hBlur)" />

      {/* orbit rings */}
      <ellipse cx="330" cy="230" rx="215" ry="170" fill="none" stroke="url(#hBrand)" strokeOpacity=".35" strokeWidth="1.5" />
      <ellipse cx="330" cy="230" rx="160" ry="215" fill="none" stroke="url(#hBrand)" strokeOpacity=".2" strokeWidth="1.5" />

      {/* the neural core */}
      <g>
        <circle cx="330" cy="230" r="70" fill="url(#hBrand)" opacity=".16" />
        <circle cx="330" cy="230" r="52" fill="url(#hBrand)" opacity=".3" />
        <circle cx="330" cy="230" r="36" fill="url(#hBrand)" />
        <text x="330" y="240" textAnchor="middle" fontSize="24" fontWeight="800" fill="#fff">AI</text>

        {[[330,130],[452,196],[420,330],[240,330],[208,196]].map(([x,y],i)=>(
          <g key={i}>
            <line x1="330" y1="230" x2={x} y2={y} stroke="url(#hBrand)" strokeOpacity=".5" strokeWidth="1.6" />
            <circle cx={x} cy={y} r="7" fill="#38BDF8" />
            <circle cx={x} cy={y} r="14" fill="#38BDF8" opacity=".18" />
          </g>
        ))}
      </g>

      {/* floating panel: a live board */}
      <g className="float-a" transform="translate(28 60)">
        <rect width="196" height="128" rx="18" fill="url(#hGlass)" stroke="#FFFFFF" strokeOpacity=".18" />
        <rect x="16" y="16" width="80" height="9" rx="4.5" fill="#FFFFFF" opacity=".5" />
        <path d="M18 88 C 52 44, 82 108, 112 66 S 160 40, 178 62"
          fill="none" stroke="url(#hBrand)" strokeWidth="5" strokeLinecap="round" />
        <circle cx="178" cy="62" r="7" fill="#A855F7" />
        <rect x="16" y="104" width="60" height="8" rx="4" fill="#38BDF8" opacity=".55" />
        <rect x="84" y="104" width="44" height="8" rx="4" fill="#A855F7" opacity=".45" />
      </g>

      {/* floating panel: the score */}
      <g className="float-b" transform="translate(408 62)">
        <rect width="176" height="112" rx="18" fill="url(#hGlass)" stroke="#FFFFFF" strokeOpacity=".18" />
        <text x="22" y="48" fontSize="30" fontWeight="800" fill="#4ADE80">92%</text>
        <rect x="22" y="62" width="130" height="9" rx="4.5" fill="#FFFFFF" opacity=".16" />
        <rect x="22" y="62" width="120" height="9" rx="4.5" fill="#4ADE80" />
        <rect x="22" y="82" width="86" height="8" rx="4" fill="#FFFFFF" opacity=".3" />
      </g>

      {/* floating panel: a generated question */}
      <g className="float-c" transform="translate(120 356)">
        <rect width="250" height="126" rx="18" fill="url(#hGlass)" stroke="#FFFFFF" strokeOpacity=".18" />
        <circle cx="36" cy="36" r="17" fill="url(#hBrand)" />
        <text x="36" y="42" textAnchor="middle" fontSize="15" fontWeight="800" fill="#fff">AI</text>
        <rect x="64" y="26" width="150" height="10" rx="5" fill="#FFFFFF" opacity=".45" />
        <rect x="64" y="44" width="104" height="8" rx="4" fill="#FFFFFF" opacity=".22" />
        <rect x="22" y="74" width="206" height="9" rx="4.5" fill="#38BDF8" opacity=".5" />
        <rect x="22" y="92" width="132" height="9" rx="4.5" fill="#4ADE80" opacity=".6" />
      </g>

      {/* floating panel: participants */}
      <g className="float-b" transform="translate(400 356)">
        <rect width="188" height="112" rx="18" fill="url(#hGlass)" stroke="#FFFFFF" strokeOpacity=".18" />
        {[0,1,2].map((i)=>(
          <g key={i} transform={`translate(20 ${20 + i*30})`}>
            <circle cx="12" cy="12" r="11" fill="url(#hBrand)" opacity={0.85 - i*0.2} />
            <rect x="32" y="7" width={112 - i*22} height="9" rx="4.5" fill="#FFFFFF" opacity=".26" />
          </g>
        ))}
      </g>
    </svg>
  </Box>
);

export default HeroArt;
